import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Grid, CircularProgress, IconButton, TextField, Chip, Paper, Avatar
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Close as CloseIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { doc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase';
import { AuthContext } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { 
  getPaymentStatusLabel, 
  getDeliveryStatusLabel, 
  getPaymentStatusColor, 
  getDeliveryStatusColor,
  getSourceLabel,
  PAYMENT_STATUSES,
  DELIVERY_STATUSES
} from '../../constants/salesConstants';

import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';

const SaleDetails = ({ sale, onClose, onUpdate, onViewProduct }) => {
  const { isAdmin, currentUser } = useContext(AuthContext);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  // Stan dla edytowalnych pól jest inicjowany na podstawie propsa 'sale'
  const [currentPaymentStatus, setCurrentPaymentStatus] = useState(sale?.payment_status || 'pending');
  const [currentDeliveryStatus, setCurrentDeliveryStatus] = useState(sale?.delivery_status || 'pending');
  const [currentNotes, setCurrentNotes] = useState(sale?.notes || '');

  // Efekt do resetowania stanu wewnętrznego, gdy zmienia się wybrana sprzedaż
  useEffect(() => {
    if (sale) {
      setCurrentPaymentStatus(sale.payment_status || 'pending');
      setCurrentDeliveryStatus(sale.delivery_status || 'pending');
      setCurrentNotes(sale.notes || '');
      setEditMode(false); // Zawsze zaczynaj w trybie podglądu
    }
  }, [sale]);

  const handleSave = async () => {
    if (!sale) return;
    setSaving(true);
    try {
      const saleRef = doc(db, 'sales', sale.id);
      await updateDoc(saleRef, {
        payment_status: currentPaymentStatus,
        delivery_status: currentDeliveryStatus,
        notes: currentNotes,
      });

      if (onUpdate) {
        onUpdate({ 
          ...sale, 
          payment_status: currentPaymentStatus,
          delivery_status: currentDeliveryStatus,
          notes: currentNotes,
        });
      }
      setEditMode(false);
    } catch (error) {
      console.error("Błąd podczas zapisywania zmian:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!sale || !isAdmin) return;
    setApproving(true);
    try {
      const saleRef = doc(db, 'sales', sale.id);
      const approvalDate = new Date(); // Używamy konkretnej daty

      // Aktualizacja statusu sprzedaży
      await updateDoc(saleRef, {
        status: 'completed',
        completedAt: approvalDate, // Używamy tej samej daty
      });

      // Aktualizacja historii zakupów klienta
      if (sale.client_id) {
        const clientRef = doc(db, 'clients', sale.client_id);
        const purchaseRecord = {
          saleId: sale.id,
          amount: sale.amount,
          date: approvalDate, // Używamy tej samej daty
          productId: sale.product_id,
          productName: getProductName(),
        };
        await updateDoc(clientRef, {
          purchaseHistory: arrayUnion(purchaseRecord),
        });
      }

      if (onUpdate) {
        onUpdate({ 
          ...sale, 
          status: 'completed',
          completedAt: approvalDate, // Aktualizujemy lokalny stan tą samą datą
        });
      }
      onClose(); // Zamknij okno po zatwierdzeniu
    } catch (error) {
      console.error("Błąd podczas zatwierdzania transakcji:", error);
    } finally {
      setApproving(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(price || 0);
  };

  // Komponent nie renderuje się, jeśli nie ma danych sprzedaży
  if (!sale) {
    return null;
  }

  // Destrukturyzacja danych bezpośrednio z propsa 'sale'
  const { 
    transaction_date,
    employeeName, // To pole jest już dodane w SalesPage.js
    clientName,   // To pole jest już dodane w SalesPage.js
    amount, // Poprawiona nazwa pola kwoty
    location,
    productDetails, // Ten obiekt jest już dodany w SalesPage.js
    product_id // Poprawiona nazwa pola ID produktu
  } = sale;

  // Poprawiona logika do tworzenia nazwy produktu i pobierania zdjęcia
  const getProductName = () => {
    if (!productDetails) return 'Brak nazwy';
    if (productDetails.category === 'watches') {
      return `${productDetails.brand || ''} ${productDetails.model || ''}`.trim();
    }
    return productDetails.jewelry_name || productDetails.name || 'Biżuteria';
  };

  const product_name = getProductName();
  const product_image = productDetails?.images && productDetails.images.length > 0 ? productDetails.images[0] : null;
  const product_diameter = productDetails?.diameter;

  const canEdit = isAdmin || (currentUser && currentUser.uid === sale.employee_id);

  const isApprovable = isAdmin && 
    !['pending_payment', 'pending', 'cancelled', 'refunded'].includes(sale.payment_status) && 
    !['pending_delivery', 'pending', 'cancelled'].includes(sale.delivery_status) &&
    sale.status !== 'completed';

  return (
    <>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Szczegóły Sprzedaży - ID: {sale.id.substring(0, 8)}
        <Box>
          {canEdit && !editMode && (
            <IconButton onClick={() => setEditMode(true)}>
              <EditIcon />
            </IconButton>
          )}
          {canEdit && editMode && (
            <IconButton onClick={handleSave} disabled={saving}>
              <SaveIcon />
            </IconButton>
          )}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Sekcja produktu */}
          <Grid item xs={12} md={5}>
            <Paper elevation={3} sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Typography variant="h6" gutterBottom>Produkt</Typography>
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                {product_image ? (
                  <Avatar 
                    src={product_image} 
                    alt={product_name}
                    variant="rounded"
                    sx={{ 
                      width: 200, 
                      height: 200, 
                      mb: 2, 
                      backgroundColor: '#000' // Czarne tło dla PNG
                    }}
                  />
                ) : (
                  <Box sx={{ width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f0f0f0', mb: 2 }}>
                    <Typography color="textSecondary">Brak zdjęcia</Typography>
                  </Box>
                )}
                <Typography variant="h6" component="div" align="center">{product_name}</Typography>
                {product_diameter && <Typography color="textSecondary" align="center">{product_diameter} mm</Typography>}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                <Typography variant="body1">Cena sprzedaży:</Typography>
                <Typography variant="h6" component="div" color="primary">{formatPrice(amount)}</Typography>
              </Box>
              {product_id && (
                <Button 
                  startIcon={<ViewIcon />} 
                  onClick={() => onViewProduct(product_id)} 
                  fullWidth 
                  sx={{ mt: 2 }}
                >
                  Zobacz szczegóły produktu
                </Button>
              )}
            </Paper>
          </Grid>

          {/* Sekcja podsumowania i statusów */}
          <Grid item xs={12} md={7}>
            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>Podsumowanie</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}><Typography variant="body2">Klient:</Typography></Grid>
                <Grid item xs={6}><Typography variant="body1">{clientName || 'Brak danych'}</Typography></Grid>
                
                <Grid item xs={6}><Typography variant="body2">Sprzedawca:</Typography></Grid>
                <Grid item xs={6}><Typography variant="body1">{employeeName || 'Brak danych'}</Typography></Grid>

                <Grid item xs={6}><Typography variant="body2">Data transakcji:</Typography></Grid>
                <Grid item xs={6}><Typography variant="body1">{transaction_date ? format(transaction_date, 'd MMMM yyyy, HH:mm', { locale: pl }) : 'Brak'}</Typography></Grid>

                <Grid item xs={6}><Typography variant="body2">Lokalizacja:</Typography></Grid>
                <Grid item xs={6}><Chip label={getSourceLabel(location)} size="small" /></Grid>
              </Grid>
            </Paper>

            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Statusy i Notatki</Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" gutterBottom>Status płatności:</Typography>
                  {editMode ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={currentPaymentStatus}
                      onChange={(e) => setCurrentPaymentStatus(e.target.value)}
                      SelectProps={{ native: true }}
                    >
                      {Object.entries(PAYMENT_STATUSES).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </TextField>
                  ) : (
                    <Chip 
                      label={getPaymentStatusLabel(currentPaymentStatus)} 
                      color={getPaymentStatusColor(currentPaymentStatus)} 
                    />
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" gutterBottom>Status dostawy:</Typography>
                  {editMode ? (
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={currentDeliveryStatus}
                      onChange={(e) => setCurrentDeliveryStatus(e.target.value)}
                      SelectProps={{ native: true }}
                    >
                      {Object.entries(DELIVERY_STATUSES).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ))}
                    </TextField>
                  ) : (
                    <Chip 
                      label={getDeliveryStatusLabel(currentDeliveryStatus)} 
                      color={getDeliveryStatusColor(currentDeliveryStatus)} 
                    />
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" gutterBottom>Notatki:</Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={currentNotes}
                      onChange={(e) => setCurrentNotes(e.target.value)}
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', p: 1, border: '1px solid #eee', borderRadius: 1, minHeight: '60px' }}>
                      {currentNotes || 'Brak notatek'}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        {isAdmin && editMode && (
          <Button onClick={() => setEditMode(false)} color="secondary">
            Anuluj
          </Button>
        )}
        {isApprovable && (
          <Button 
            onClick={handleApprove} 
            variant="contained" 
            color="success"
            disabled={approving}
            startIcon={approving ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          >
            Zatwierdź transakcję
          </Button>
        )}

        {canEdit && editMode ? (
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : 'Zapisz zmiany'}
          </Button>
        ) : (
          <Button onClick={onClose} variant="contained">
            Zamknij
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default SaleDetails;
